import { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ConfirmDialog,
  type ContextMenuItem,
  MediaCard,
  toast,
} from '@core/components';
import { useConfirmDialog } from '@core/hooks/useConfirmDialog';
import { useCrudList } from '@core/hooks/useCrudList';
import type { StoleCut } from '../../../api/stoleCut';
import { deleteStoleCut, reorderStoleCuts } from '../../../api/stoleCut';
import {
  type Vestment,
  getAllVestments,
  reorderVestments,
} from '../../../api/vestment';
import GroupedSortableSettings, {
  useSelectedGroup,
} from '../../components/GroupedSortableSettings';
import StoleCutDialog from '../../components/StoleCutDialog';
import VestmentDialog from '../../components/VestmentDialog';
import { useAppContext } from '../../context/AppContext';
import { VestmentCategory } from '../../enum/vestmentCategory';
import { getCategory } from '../../enum/vestmentType';
import { getImageAspectRatio, getImageThumbnailUrl } from '../../utils/image';
import { getStoleFeatures } from '../../utils/stole';

const Stoles = () => {
  const { refreshContext } = useAppContext();
  const [imageDialog, setImageDialog] = useState<{
    open: boolean;
    vestment: Vestment | null;
  }>({ open: false, vestment: null });
  const [cutDialog, setCutDialog] = useState<{
    open: boolean;
    cut: StoleCut | null;
  }>({ open: false, cut: null });

  const loadStoles = useCallback(
    async () =>
      (await getAllVestments()).filter(
        (vestment) =>
          !vestment.isHidden &&
          getCategory(vestment.type) === VestmentCategory.Stole
      ),
    []
  );
  const reorderStoles = useCallback(
    (ids: number[]) => reorderVestments(VestmentCategory.Stole, ids),
    []
  );

  const {
    items: stoles,
    setItems: setStoles,
    loading,
    load,
    rowReorder,
  } = useCrudList<Vestment>({
    loadItems: loadStoles,
    reorderItems: reorderStoles,
    loadErrorMessage: 'Failed to load stoles',
    reorderErrorMessage: 'Failed to reorder stoles',
    refreshContext,
  });

  const { selectedGroup: selectedStole, selectGroup: selectStole } =
    useSelectedGroup(stoles, 'stole');
  const allowsCreateCut =
    getStoleFeatures(selectedStole?.type)?.allowsCreateCut ?? false;

  const cutDelete = useConfirmDialog<StoleCut>({
    onConfirm: (cut) => deleteStoleCut(cut.id),
    onSuccess: load,
    successMessage: 'Type deleted',
    errorMessage: 'Failed to delete type',
  });

  const cutMenuItems = (cut: StoleCut): ContextMenuItem[] => [
    { label: 'Edit', onClick: () => setCutDialog({ open: true, cut }) },
    {
      label: 'Delete',
      destructive: true,
      onClick: () => cutDelete.request(cut),
    },
  ];

  const onCutsReorder = (stole: Vestment, next: StoleCut[]) => {
    setStoles((prev) =>
      prev.map((item) =>
        item.id === stole.id ? { ...item, stoleCuts: next } : item
      )
    );
    reorderStoleCuts(
      stole.id,
      next.map((cut) => cut.id)
    )
      .then(refreshContext)
      .catch(() => {
        toast.error('Failed to reorder types');
        void load();
      });
  };

  return (
    <GroupedSortableSettings<Vestment, StoleCut>
      title="Stoles"
      loading={loading}
      groups={stoles}
      selectedGroup={selectedStole}
      onSelectGroup={selectStole}
      groupReorder={rowReorder}
      groupLabel="Stole"
      getGroupMenuItems={(vestment) => [
        {
          label: 'Edit',
          onClick: () => setImageDialog({ open: true, vestment }),
        },
      ]}
      emptyState={{ description: 'No stoles are configured.' }}
      getItems={(stole) =>
        getStoleFeatures(stole.type)?.hasCuts ? stole.stoleCuts : []
      }
      getItemsCaption={(stole, count) => {
        if (!getStoleFeatures(stole.type)?.hasCuts) {
          return 'This stole has no types.';
        }
        return count === 0
          ? 'No types yet.'
          : `${count} type${count === 1 ? '' : 's'}`;
      }}
      onItemsReorder={onCutsReorder}
      renderItem={(cut, onMenuClick) => (
        <MediaCard
          content={
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="caption" textAlign="center">
                {cut.name}
              </Typography>
            </Box>
          }
          imageSrc={getImageThumbnailUrl(cut)}
          aspectRatio={getImageAspectRatio(cut) ?? 1}
          fullHeight
          onClick={() => setCutDialog({ open: true, cut })}
          onMenuClick={onMenuClick}
        />
      )}
      getItemMenuItems={cutMenuItems}
      addItem={
        allowsCreateCut
          ? {
              label: 'Add type',
              onClick: () => setCutDialog({ open: true, cut: null }),
            }
          : undefined
      }
    >
      {imageDialog.vestment ? (
        <VestmentDialog
          open={imageDialog.open}
          onClose={() =>
            setImageDialog((current) => ({ ...current, open: false }))
          }
          vestment={imageDialog.vestment}
          onSuccess={() => void load()}
        />
      ) : null}
      {selectedStole ? (
        <StoleCutDialog
          open={cutDialog.open}
          onClose={() => setCutDialog({ open: false, cut: null })}
          vestment={selectedStole}
          cut={cutDialog.cut}
          onSuccess={() => void load()}
        />
      ) : null}
      <ConfirmDialog
        open={cutDelete.open}
        title="Delete Type"
        content={`Are you sure you want to delete "${cutDelete.item?.name}"?`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={() => void cutDelete.confirm()}
        onCancel={cutDelete.cancel}
        loading={cutDelete.loading}
      />
    </GroupedSortableSettings>
  );
};

export default Stoles;
